import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState } from 'react';
import { X, Calendar, Shield, CheckCircle, ArrowLeft, ArrowRight, Star, Lock, Users, Plus, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import AmountInput from './AmountInput';
import WizardDateField from './WizardDateField';
import { bankService } from '../api/bankService';

const SecuriteSocialeWizard = ({ isOpen, onClose, onSubmit, editMode = false, initialData = null, currentUser = null }) => {
  const [currentStep, setCurrentStep] = useState(editMode ? 4 : 1);
  const [formData, setFormData] = useState(
    editMode && initialData
      ? {
          category: initialData.category || initialData.chifa_category || 'CNAS',
          date: initialData.date ? parseSafeDate(initialData.date) : new Date(),
          amount: initialData.amount || 0,
          description: initialData.description || '',
          reference: initialData.reference || '',
          bordereau_number: initialData.bordereau_number || '',
          liquidation_date: initialData.liquidation_date ? new Date(initialData.liquidation_date) : null,
          validation_date: initialData.validation_date ? new Date(initialData.validation_date) : null,
          virement_date: initialData.virement_date ? new Date(initialData.virement_date) : null,
          virement_amount: initialData.virement_amount ?? '',
          montant_bordereau: initialData.montant_bordereau ?? '',
          montant_associe: initialData.montant_associe ?? '',
          montant_officiel: initialData.montant_officiel ?? '',
          montant_majoration: initialData.montant_majoration ?? '',
          montant_officine: initialData.montant_officine ?? '',
          montant_rejete: initialData.montant_rejete ?? '',
          encaissement_date: initialData.encaissement_date ? new Date(initialData.encaissement_date) : null,
          is_paid: initialData.is_paid === true,
          bank_account_id: initialData.bank_account_id || '',
          bank_amount: initialData.bank_amount ?? '',
        }
      : {
          category: '',
          date: new Date(),
          amount: 0,
          description: '',
          reference: '',
          bordereau_number: '',
          liquidation_date: null,
          validation_date: null,
          virement_date: null,
          virement_amount: '',
          montant_bordereau: '',
          montant_associe: '',
          montant_officiel: '',
          montant_majoration: '',
          montant_officine: '',
          montant_rejete: '',
          encaissement_date: null,
          is_paid: false,
          bank_account_id: '',
          bank_amount: '',
        }
  );
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  // Fetch bank accounts when wizard opens
  React.useEffect(() => {
    if (isOpen) {
      const activeUser = currentUser || (() => {
        try { return JSON.parse(sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user')); } catch { return null; }
      })();
      bankService.getAccounts(activeUser)
        .then(data => {
          const accounts = Array.isArray(data) ? data : (data?.accounts || data?.bank_accounts || []);
          setBankAccounts(accounts);
        })
        .catch(err => console.error("Error fetching bank accounts", err));
    }
  }, [isOpen, currentUser]);

  // Réinitialiser ou pré-remplir selon le mode
  React.useEffect(() => {
    if (isOpen) {
      if (editMode && initialData) {
        setCurrentStep(4);
        setFormData({
          category: initialData.category || initialData.chifa_category || 'CNAS',
          date: initialData.date ? parseSafeDate(initialData.date) : new Date(),
          amount: initialData.amount || 0,
          description: initialData.description || '',
          reference: initialData.reference || '',
          bordereau_number: initialData.bordereau_number || '',
          liquidation_date: initialData.liquidation_date ? new Date(initialData.liquidation_date) : null,
          validation_date: initialData.validation_date ? new Date(initialData.validation_date) : null,
          virement_date: initialData.virement_date ? new Date(initialData.virement_date) : null,
          virement_amount: initialData.virement_amount ?? '',
          montant_bordereau: initialData.montant_bordereau ?? '',
          montant_associe: initialData.montant_associe ?? '',
          montant_officiel: initialData.montant_officiel ?? '',
          montant_majoration: initialData.montant_majoration ?? '',
          montant_officine: initialData.montant_officine ?? '',
          montant_rejete: initialData.montant_rejete ?? '',
          encaissement_date: initialData.encaissement_date ? new Date(initialData.encaissement_date) : null,
          is_paid: initialData.is_paid === true,
          bank_account_id: initialData.bank_account_id || '',
          bank_amount: initialData.bank_amount ?? '',
        });
      } else {
        setCurrentStep(1);
        setFormData({
          category: '',
          date: new Date(),
          amount: 0,
          description: '',
          reference: '',
          bordereau_number: '',
          liquidation_date: null,
          validation_date: null,
          virement_date: null,
          virement_amount: '',
          montant_bordereau: '',
          montant_associe: '',
          montant_officiel: '',
          montant_majoration: '',
          montant_officine: '',
          montant_rejete: '',
          encaissement_date: null,
          is_paid: false,
          bank_account_id: '',
          bank_amount: '',
        });
      }
    }
  }, [isOpen, editMode, initialData]);

  // Catégories de sécurité sociale avec leurs configurations
  const categories = [
    {
      id: 'CNAS',
      label: 'CNAS',
      description: 'Assurance maladie nationale',
      icon: Star,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    {
      id: 'HORS CNAS',
      label: 'HORS CNAS',
      description: 'Soins non couverts par CNAS',
      icon: Lock,
      color: 'bg-purple-400',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    {
      id: 'CASNOS',
      label: 'CASNOS',
      description: 'Travailleurs non salariés',
      icon: Users,
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    },
    {
      id: 'HORS CASNOS',
      label: 'HORS CASNOS',
      description: 'Hors couverture CASNOS',
      icon: AlertTriangle,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    {
      id: 'Caisse militaire',
      label: 'Caisse militaire',
      description: 'Remboursements médicaux',
      icon: Plus,
      color: 'bg-purple-400',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    }
  ];

  const handleCategorySelect = (categoryId) => {
    setFormData({ ...formData, category: categoryId });
  };

  const handleDateSelect = (date) => {
    // Preserve current time (HH:mm:ss) on the picked day so entries keep a real timestamp
    const now = new Date();
    const stamped = new Date(date);
    stamped.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
    setFormData({ ...formData, date: stamped });
    setShowCalendar(false);
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const startDayOfWeek = getDay(monthStart);
    
    // Jours de la semaine commençant par Samedi
    const weekDays = ['S', 'D', 'L', 'M', 'M', 'J', 'V'];
    
    // Ajuster pour commencer par Samedi
    const adjustedStartDay = (startDayOfWeek + 1) % 7;
    
    return (
      <div className="bg-gray-50 rounded-2xl p-6 border-2 border-purple-200">
        {/* Header du calendrier */}
        <div className="flex items-center justify-between mb-6">
          <button
            type="button"
            onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <ChevronLeft size={20} />
          </button>
          <h4 className="text-xl font-bold text-gray-900 capitalize">
            {format(calendarMonth, 'MMMM yyyy', { locale: fr })}
          </h4>
          <button
            type="button"
            onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Jours de la semaine */}
        <div className="grid grid-cols-7 gap-2 mb-3">
          {weekDays.map((day, idx) => (
            <div key={idx} className="text-center text-sm font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        {/* Jours du mois */}
        <div className="grid grid-cols-7 gap-2">
          {/* Cellules vides pour aligner le premier jour */}
          {Array.from({ length: adjustedStartDay }).map((_, idx) => (
            <div key={`empty-${idx}`} />
          ))}
          
          {/* Jours du mois */}
          {daysInMonth.map((day) => {
            const isSelected = formData.date && 
              format(day, 'yyyy-MM-dd') === format(formData.date, 'yyyy-MM-dd');
            const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
            
            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => handleDateSelect(day)}
                className={`
                  h-12 rounded-xl font-medium text-base transition-all
                  ${isSelected
                    ? 'bg-purple-500 text-white'
                    : isToday
                    ? 'bg-purple-100 text-purple-900 border-2 border-purple-300'
                    : 'text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>

        {/* Date sélectionnée */}
        {formData.date && (
          <div className="mt-6 text-center">
            <p className="text-lg font-bold text-gray-900 capitalize">
              {format(formData.date, 'EEEE d MMM. yyyy', { locale: fr })}
            </p>
          </div>
        )}

        {/* Bouton Confirmer */}
        <button
          type="button"
          onClick={() => setShowCalendar(false)}
          className="w-full mt-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 font-semibold hover:bg-gray-50"
        >
          Confirmer
        </button>
      </div>
    );
  };

  const handleAmountChange = (value) => {
    setFormData({ ...formData, amount: value });
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    onSubmit(formData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      category: '',
      date: new Date(),
      amount: 0,
      description: '',
      reference: ''
    });
    setCurrentStep(1);
    setShowCalendar(false);
    onClose();
  };

  if (!isOpen) return null;

  const selectedCategory = categories.find(c => c.id === formData.category);
  const today = format(new Date(), 'dd MMM. yyyy', { locale: fr });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Shield className="text-purple-600" size={24} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Paiement sécurité sociale</h2>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">{today}</span>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Stepper */}
        <div className="px-6 py-3 border-b border-gray-100">
          <div className="flex items-center justify-between max-w-xl mx-auto">
            {[
              { step: 1, label: 'Catégorie' },
              { step: 2, label: 'Date dépôt' },
              { step: 3, label: 'Montant' },
              { step: 4, label: 'Validation' }
            ].map(({ step, label }, index) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                      step < currentStep
                        ? 'bg-purple-500 text-white'
                        : step === currentStep
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {step < currentStep ? '✓' : step}
                  </div>
                  <span
                    className={`text-sm font-medium ${
                      step <= currentStep ? 'text-purple-600' : 'text-gray-400'
                    }`}
                  >
                    {label}
                  </span>
                </div>
                {index < 3 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      step < currentStep ? 'bg-purple-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          {/* Étape 1 - Catégorie */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Catégorie sécurité sociale</h3>
                <p className="text-gray-500">Sélectionnez l'organisme de couverture</p>
              </div>

              <div className="space-y-3">
                {formData.category ? (
                  <>
                    {categories.filter(c => c.id === formData.category).map((category) => {
                      const Icon = category.icon;
                      return (
                        <div key={category.id} className={`w-full p-3 rounded-xl border-2 ${category.bgColor} ${category.borderColor} flex items-center gap-4`}>
                          <div className={`w-10 h-10 rounded-lg ${category.color} flex items-center justify-center flex-shrink-0`}>
                            <Icon className="text-white" size={28} />
                          </div>
                          <div className="text-left flex-1">
                            <div className="font-bold text-lg text-gray-900">{category.label}</div>
                            <div className="text-gray-600">{category.description}</div>
                          </div>
                        </div>
                      );
                    })}
                    <button type="button" onClick={() => setFormData({ ...formData, category: '' })}
                      className="w-full py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-600 font-medium hover:bg-gray-50 flex items-center justify-center gap-2 transition-all">
                      <X size={16} /> Changer de catégorie
                    </button>
                  </>
                ) : (
                  categories.map((category) => {
                    const Icon = category.icon;
                    return (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => handleCategorySelect(category.id)}
                        className="w-full p-3 rounded-xl border-2 bg-white border-gray-200 hover:border-gray-300 transition-all flex items-center gap-4"
                      >
                        <div className={`w-10 h-10 rounded-lg ${category.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="text-white" size={28} />
                        </div>
                        <div className="text-left flex-1">
                          <div className="font-bold text-lg text-gray-900">{category.label}</div>
                          <div className="text-gray-600">{category.description}</div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <span />
                <span className="text-xs text-gray-500 font-medium">Étape 1 sur 4</span>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!formData.category}
                  className="px-5 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Continuer
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Étape 2 - Date */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Date de dépôt</h3>
                <p className="text-gray-500">Choisissez la date de dépôt du bordereau</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-3 uppercase">Date dépôt</label>
                
                {/* Champ de sélection de date */}
                <button
                  type="button"
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="w-full bg-purple-50 rounded-2xl p-6 border-2 border-purple-300 hover:bg-purple-100 transition-all"
                >
                  <div className="flex items-center gap-3 text-lg justify-between">
                    <div className="flex items-center gap-3">
                      <Calendar className="text-purple-600" size={24} />
                      <span className="text-gray-900 font-medium capitalize">
                        {format(formData.date, 'EEEE d MMM. yyyy', { locale: fr })}
                      </span>
                    </div>
                    <ChevronRight className="text-gray-400" size={20} />
                  </div>
                </button>

                {/* Calendrier déroulant */}
                {showCalendar && (
                  <div className="mt-4">
                    {renderCalendar()}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
                >
                  <ArrowLeft size={16} />
                  Retour
                </button>
                <span className="text-xs text-gray-500 font-medium">Étape 2 sur 4</span>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!formData.date}
                  className="px-5 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Continuer
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Étape 3 - Montant */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Montant bordereau & informations</h3>
                <p className="text-gray-500">Tapez directement ou utilisez le pavé</p>
              </div>

              <AmountInput value={formData.amount} onChange={handleAmountChange} accentColor="purple" />

              {/* === Suivi du bordereau (obligatoire) === */}
              <div className="border-t border-gray-200 pt-5">
                <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Suivi du bordereau</h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      N° Bordereau <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.bordereau_number}
                      onChange={(e) => setFormData({ ...formData, bordereau_number: e.target.value })}
                      placeholder="Ex : BD-2026-001"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
                      data-testid="wizard-bordereau-number"
                    />
                  </div>
                </div>
              </div>

              {/* === Dates de suivi === */}
              <div className="border-t border-gray-200 pt-5 mt-5">
                <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Dates de suivi</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Date de liquidation</label>
                    <WizardDateField
                      value={formData.liquidation_date}
                      onChange={(d) => setFormData({ ...formData, liquidation_date: d })}
                      theme="purple"
                      testid="wizard-liquidation-date"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Date de validation</label>
                    <WizardDateField
                      value={formData.validation_date}
                      onChange={(d) => setFormData({ ...formData, validation_date: d })}
                      theme="purple"
                      testid="wizard-validation-date"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Date de virement</label>
                    <WizardDateField
                      value={formData.virement_date}
                      onChange={(d) => setFormData({ ...formData, virement_date: d })}
                      theme="purple"
                      testid="wizard-virement-date"
                    />
                  </div>
                </div>
              </div>

              {/* === Montants === */}
              <div className="border-t border-gray-200 pt-5 mt-5">
                <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Montants</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Montant bordereau (DA)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.montant_bordereau}
                      onChange={(e) => setFormData({ ...formData, montant_bordereau: e.target.value })}
                      placeholder="Ex : 50000"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
                      data-testid="wizard-montant-bordereau"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Montant associée (DA)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.montant_associe}
                      onChange={(e) => setFormData({ ...formData, montant_associe: e.target.value })}
                      placeholder="Ex : 1000"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
                      data-testid="wizard-montant-associe"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Montant officiel (DA)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.montant_officiel}
                      onChange={(e) => setFormData({ ...formData, montant_officiel: e.target.value })}
                      placeholder="Ex : 50000"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
                      data-testid="wizard-montant-officiel"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Montant majoration (DA)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.montant_majoration}
                      onChange={(e) => setFormData({ ...formData, montant_majoration: e.target.value })}
                      placeholder="Ex : 500"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
                      data-testid="wizard-montant-majoration"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Montant Payé (DA)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.virement_amount}
                      onChange={(e) => setFormData({ ...formData, virement_amount: e.target.value })}
                      placeholder="Ex : 50000"
                      className="w-full p-3 border-2 border-green-200 rounded-lg focus:outline-none focus:border-green-500 text-green-700"
                      data-testid="wizard-virement-amount"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Montant officine (DA)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.montant_officine}
                      onChange={(e) => setFormData({ ...formData, montant_officine: e.target.value })}
                      placeholder="Ex : 50000"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
                      data-testid="wizard-montant-officine"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Montant rejeté (DA)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.montant_rejete}
                      onChange={(e) => setFormData({ ...formData, montant_rejete: e.target.value })}
                      placeholder="Ex : 1000"
                      className="w-full p-3 border-2 border-red-200 rounded-lg focus:outline-none focus:border-red-500 text-red-600"
                      data-testid="wizard-montant-rejete"
                    />
                  </div>

                </div>
              </div>

              {/* === Suivi de banque === */}
              <div className="border-t border-gray-200 pt-5">
                <h4 className="text-sm font-semibold text-gray-700 uppercase mb-3">Suivi de banque</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Compte bancaire
                    </label>
                    <select
                      value={formData.bank_account_id}
                      onChange={(e) => setFormData({ ...formData, bank_account_id: e.target.value })}
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500 bg-white"
                    >
                      <option value="">Sélectionnez un compte...</option>
                      {bankAccounts.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {acc.bank_name} - {acc.name} ({acc.account_number})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Date d'encaissement</label>
                    <WizardDateField
                      value={formData.encaissement_date}
                      onChange={(d) => setFormData({ ...formData, encaissement_date: d })}
                      theme="purple"
                      testid="wizard-encaissement-date"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Montant (DA)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.bank_amount}
                      onChange={(e) => setFormData({ ...formData, bank_amount: e.target.value })}
                      placeholder="Ex : 50000"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
                >
                  <ArrowLeft size={16} />
                  Retour
                </button>
                <span className="text-xs text-gray-500 font-medium">Étape 3 sur 4</span>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!formData.bordereau_number?.trim() || !formData.amount}
                  className="px-5 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Continuer
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Étape 4 - Validation */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Tout est correct ?</h3>
                <p className="text-gray-500">Relisez avant de valider</p>
              </div>

              {/* Message d'info */}
              <div className="bg-purple-50 rounded-xl p-3 flex items-center gap-3">
                <CheckCircle className="text-purple-600 flex-shrink-0" size={20} />
                <span className="text-purple-700 font-medium text-sm">Sera enregistré dans votre tableau de bord</span>
              </div>

              {/* Résumé enrichi */}
              <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
                {[
                  ['Catégorie', formData.category, false, false],
                  ['N° Bordereau', formData.bordereau_number || '—', false, false],
                  ['Date dépôt', format(formData.date, 'dd MMM. yyyy', { locale: fr }), false, false],
                  ['Date de liquidation', formData.liquidation_date ? format(formData.liquidation_date, 'dd MMM. yyyy', { locale: fr }) : '—', false, false],
                  ['Date de validation', formData.validation_date ? format(formData.validation_date, 'dd MMM. yyyy', { locale: fr }) : '—', false, false],
                  ['Date de virement', formData.virement_date ? format(formData.virement_date, 'dd MMM. yyyy', { locale: fr }) : '—', false, false],
                  ['Montant bordereau', `${formData.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA`, true, false],
                  ['Montant officine', formData.montant_officine !== '' && formData.montant_officine != null ? `${parseFloat(formData.montant_officine).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA` : '—', false, false],
                  ['Montant Payé (DA)', formData.virement_amount !== '' && formData.virement_amount != null ? `${parseFloat(formData.virement_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA` : '—', true, false],
                  ['Montant rejeté', formData.montant_rejete !== '' && formData.montant_rejete != null ? `${parseFloat(formData.montant_rejete).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA` : '—', false, true],
                  ["Compte bancaire", bankAccounts.find(a => a.id === formData.bank_account_id)?.name || '—', false, false],
                  ["Date d'encaissement", formData.encaissement_date ? format(formData.encaissement_date, 'dd MMM. yyyy', { locale: fr }) : '—', false, false],
                  ["Montant encaissé (DA)", formData.bank_amount ? `${parseFloat(formData.bank_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DA` : '—', true, false],
                  (() => {
                    const isPaidComputed = formData.is_paid || !!formData.bank_account_id || (formData.virement_amount !== '' && formData.virement_amount != null && parseFloat(formData.virement_amount) > 0);
                    return ['Statut', isPaidComputed ? 'Payé' : 'Non payé', isPaidComputed, !isPaidComputed];
                  })(),
                ].map(([label, value, highlight, danger]) => (
                  <div key={label} className="flex justify-between items-center px-4 py-2.5">
                    <span className="text-sm text-gray-600">{label}</span>
                    <span className={`text-sm capitalize ${danger ? 'font-bold text-red-600' : highlight ? 'font-bold text-purple-600' : 'font-semibold text-gray-900'}`}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2 bg-white border-2 border-gray-200 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
                >
                  <ArrowLeft size={16} />
                  Modifier
                </button>
                <span className="text-xs text-gray-500 font-medium">Étape 4 sur 4</span>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-5 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold text-sm flex items-center gap-1.5 transition-colors"
                >
                  <CheckCircle size={16} />
                  Enregistrer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecuriteSocialeWizard;
